import { useGlobalQuery, useGlobalMutation } from 'state/query-client/hooks';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { refreshAccessToken } from 'features/auth/services/auth-service';
import {
    getSubscriptionPlans,
    generateCheckoutLink,
    getBillingAddress,
    updateBillingAddress,
    getCurrentPlan,
    subscribeFreePlan,
    cancelSubscription,
    updateSubscription,
    upgradeSubscriptionForUser,
    cancelAllUserSubscriptions,
    getCouponInfo,
    getPlanUpdatePrice,
    type GenerateCheckoutLinkRequest,
    type GenerateCheckoutLinkResponse,
    type UpdateBillingAddressRequest,
    type UpdateBillingAddressResponse,
    type SubscribeFreePlanResponse,
    type CancelSubscriptionResponse,
    type UpdateSubscriptionRequest,
    type UpdateSubscriptionResponse,
    type UpgradeSubscriptionForUserRequest,
    type UpgradeSubscriptionForUserResponse,
    type CancelAllUserSubscriptionsRequest,
    type CancelAllUserSubscriptionsResponse,
    type GetCouponInfoRequest,
    type GetCouponInfoResponse,
    type GetPlanUpdatePriceRequest,
} from '../services/subscription-service';

/**
 * Custom hook to fetch all subscription plans.
 *
 * @returns {Object} The query result for getting subscription plans.
 *
 * @example
 * const { data, error, isLoading } = useSubscriptionPlans();
 */
export function useSubscriptionPlans() {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['subscription-plans'],
        queryFn: () => getSubscriptionPlans(configurationSettings, { page: 0, pageSize: 100, sort: { property: "Order", isDescending: false } }),
        enabled: !!configurationSettings,
    });
}

/**
 * Custom hook to generate checkout link for subscription.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when checkout link generation is successful.
 * @param {Function} options.onError - Callback function called when checkout link generation fails.
 * @returns {Object} The mutation result for generating checkout link.
 *
 * @example
 * const { mutate } = useGenerateCheckoutLink({
 *   onSuccess: (response) => {
 *     window.location.href = response.checkoutUrl;
 *   }
 * });
 */
export function useGenerateCheckoutLink(options?: {
    onSuccess?: (data: GenerateCheckoutLinkResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: GenerateCheckoutLinkRequest) =>
            generateCheckoutLink(data, configurationSettings),
        onSuccess: (data) => {
            if (options?.onSuccess) {
                options.onSuccess(data);
            }
        },
        onError: (error: any) => {
            if (error?.error?.errors?.Name === 'Name already exists') {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('ORGANIZATION_ALREADY_EXISTS'),
                });
            }
            else if (error?.error?.errors?.message === 'Plan update not allowed for already cancelled plan') {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('PLAN_UPDATE_NOT_ALLOWED_CANCELLED_SUBSCRIPTION'),
                });
            }
            else {
                toast({
                    title: t('CHECKOUT_ERROR'),
                    description: t('FAILED_GENERATE_CHECKOUT_LINK'),
                    variant: 'destructive',
                });
            }

            if (options?.onError) {
                options.onError(error);
            }
        },
    });
}

/**
 * Custom hook to fetch billing address for prefilling checkout form.
 *
 * @returns {Object} The query result for getting billing address.
 */
export function useBillingAddress() {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['billing-address'],
        queryFn: () => getBillingAddress(configurationSettings),
        retry: 1, // Only retry once since this is for prefilling
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

/**
 * Custom hook to update billing address.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when update is successful.
 * @param {Function} options.onError - Callback function called when update fails.
 * @returns {Object} The mutation result for updating billing address.
 *
 * @example
 * const { mutate } = useUpdateBillingAddress({
 *   onSuccess: (response) => {
 *     toast.success('Billing address updated successfully');
 *   }
 * });
 */
export function useUpdateBillingAddress(options?: {
    onSuccess?: (data: UpdateBillingAddressResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: UpdateBillingAddressRequest) =>
            updateBillingAddress(data, configurationSettings),
        onSuccess: (data) => {
            if (data.isSuccess) {
                toast({
                    title: t('SUCCESS'),
                    description: t('BILLING_ADDRESS_UPDATED'),
                });
                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors || t('FAILED_UPDATE_BILLING_ADDRESS'),
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors || t('UPDATE_FAILED')));
            }
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_UPDATE_BILLING_ADDRESS'),
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to fetch the current user's active subscription plan.
 *
 * @returns {Object} The query result for getting current subscription plan.
 *
 * @example
 * const { data: currentPlan, error, isLoading } = useCurrentPlan();
 */
export function useCurrentPlan() {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['current-plan'],
        queryFn: () => getCurrentPlan(configurationSettings),
        enabled: !!configurationSettings,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });
}

/**
 * Custom hook to subscribe to the free plan.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when subscription is successful.
 * @param {Function} options.onError - Callback function called when subscription fails.
 * @returns {Object} The mutation result for subscribing to free plan.
 *
 * @example
 * const { mutate } = useSubscribeFreePlan({
 *   onSuccess: (response) => {
 *     console.log('Successfully subscribed to free plan');
 *   }
 * });
 */
export function useSubscribeFreePlan(options?: {
    onSuccess?: (data: SubscribeFreePlanResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: () =>
            subscribeFreePlan(configurationSettings),
        onSuccess: async (data) => {
            if (data.isSuccess) {
                try {
                    // Refresh access token after successful free plan subscription
                    await refreshAccessToken(configurationSettings);
                } catch (tokenError) {
                    console.warn('Failed to refresh access token after free plan subscription:', tokenError);
                    // Don't fail the entire operation if token refresh fails
                }

                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors || t('FAILED_SUBSCRIBE_FREE_PLAN'),
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors || t('SUBSCRIPTION_FAILED')));
            }
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_SUBSCRIBE_FREE_PLAN_RETRY'),
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to cancel a subscription.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when cancellation is successful.
 * @param {Function} options.onError - Callback function called when cancellation fails.
 * @returns {Object} The mutation result for cancelling subscription.
 *
 * @example
 * const { mutate } = useCancelSubscription({
 *   onSuccess: (response) => {
 *     console.log('Subscription cancelled successfully');
 *   }
 * });
 */
export function useCancelSubscription(options?: {
    onSuccess?: (data: CancelSubscriptionResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: () =>
            cancelSubscription(configurationSettings),
        onSuccess: (data) => {
            if (data.isSuccess) {
                toast({
                    title: t('SUCCESS'),
                    description: t('SUBSCRIPTION_CANCELLED_SUCCESSFULLY'),
                });
                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors || t('FAILED_CANCEL_SUBSCRIPTION'),
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors || t('CANCELLATION_FAILED')));
            }
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_CANCEL_SUBSCRIPTION_RETRY'),
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to update a subscription to a new plan.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when update is successful.
 * @param {Function} options.onError - Callback function called when update fails.
 * @returns {Object} The mutation result for updating subscription.
 *
 * @example
 * const { mutate } = useUpdateSubscription({
 *   onSuccess: (response) => {
 *     console.log('Subscription updated successfully');
 *   }
 * });
 */
export function useUpdateSubscription(options?: {
    onSuccess?: (data: UpdateSubscriptionResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: UpdateSubscriptionRequest) =>
            updateSubscription(data, configurationSettings),
        onSuccess: async (data) => {
            if (data.isSuccess) {
                try {
                    // Refresh access token after successful plan update
                    await refreshAccessToken(configurationSettings);
                } catch (tokenError) {
                    console.warn('Failed to refresh access token after plan update:', tokenError);
                    // Don't fail the entire operation if token refresh fails
                }
                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors || t('FAILED_UPDATE_SUBSCRIPTION'),
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors || t('UPDATE_FAILED')));
            }
        },
        onError: (error: any) => {
            if (error?.error?.errors?.message === 'User organization add failed') {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('ORGANIZATION_ALREADY_EXISTS'),
                });
            }
            else {
                toast({
                    title: t('ERROR'),
                    description: t('FAILED_UPDATE_SUBSCRIPTION_RETRY'),
                    variant: 'destructive',
                });
            }
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to upgrade a user's subscription plan (admin function).
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when upgrade is successful.
 * @param {Function} options.onError - Callback function called when upgrade fails.
 * @returns {Object} The mutation result for upgrading user subscription.
 *
 * @example
 * const { mutate } = useUpgradeSubscriptionForUser({
 *   onSuccess: (response) => {
 *     console.log('User subscription upgraded successfully');
 *   }
 * });
 */
export function useUpgradeSubscriptionForUser(options?: {
    onSuccess?: (data: UpgradeSubscriptionForUserResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: UpgradeSubscriptionForUserRequest) =>
            upgradeSubscriptionForUser(data, configurationSettings),
        onSuccess: (data) => {
            if (data.isSuccess) {
                toast({
                    title: t('SUCCESS'),
                    description: t('PLAN_CHANGED_SUCCESSFULLY') || 'Plan changed successfully',
                });
                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors?.message || t('FAILED_TO_CHANGE_PLAN') || 'Failed to change plan',
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors?.message || t('UPGRADE_FAILED')));
            }
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_TO_CHANGE_PLAN') || 'Failed to change plan',
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to cancel all subscriptions for a specific user (admin function).
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when cancellation is successful.
 * @param {Function} options.onError - Callback function called when cancellation fails.
 * @returns {Object} The mutation result for canceling all user subscriptions.
 *
 * @example
 * const { mutate } = useCancelAllUserSubscriptions({
 *   onSuccess: (response) => {
 *     console.log('All user subscriptions cancelled successfully');
 *   }
 * });
 */
export function useCancelAllUserSubscriptions(options?: {
    onSuccess?: (data: CancelAllUserSubscriptionsResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: CancelAllUserSubscriptionsRequest) =>
            cancelAllUserSubscriptions(data, configurationSettings),
        onSuccess: (data) => {
            if (data.isSuccess) {
                toast({
                    title: t('SUCCESS'),
                    description: t('USER_SUBSCRIPTIONS_CANCELLED_SUCCESSFULLY') || 'User subscriptions cancelled successfully',
                });
                options?.onSuccess?.(data);
            } else {
                toast({
                    title: t('ERROR'),
                    description: data.errors?.message || t('FAILED_TO_CANCEL_USER_SUBSCRIPTIONS') || 'Failed to cancel user subscriptions',
                    variant: 'destructive',
                });
                options?.onError?.(new Error(data.errors?.message || t('CANCELLATION_FAILED')));
            }
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_TO_CANCEL_USER_SUBSCRIPTIONS') || 'Failed to cancel user subscriptions',
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to get coupon information for a specific promo code and plan.
 *
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when coupon validation is successful.
 * @param {Function} options.onError - Callback function called when coupon validation fails.
 * @returns {Object} The mutation result for getting coupon information.
 *
 * @example
 * const { mutate } = useCouponInfo({
 *   onSuccess: (response) => {
 *     console.log('Coupon is valid with discount:', response.discountPercent);
 *   }
 * });
 */
export function useCouponInfo(options?: {
    onSuccess?: (data: GetCouponInfoResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    //const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: GetCouponInfoRequest) =>
            getCouponInfo(data, configurationSettings),
        onSuccess: (data) => {
            if (data.isSuccess && data.couponDiscountPercent !== null) {
                /* toast({
                    title: t('SUCCESS'),
                    description: t('COUPON_APPLIED_SUCCESSFULLY') || `Coupon applied! ${data.discountPercent}% discount`,
                }); */
                options?.onSuccess?.(data);
            } else {
                // Don't show toast for coupon errors, let the component handle it
                options?.onError?.(new Error(data.errors?.message || t('INVALID_COUPON')));
            }
        },
        onError: (error) => {
            // Don't show toast for network errors, let the component handle it
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to get the updated price for a plan change/upgrade.
 *
 * @param {GetPlanUpdatePriceRequest} request - The plan update price request data containing PlanId and PlanItemId.
 * @returns {Object} The query result for getting plan update price.
 *
 * @example
 * const { data, isLoading, error } = usePlanUpdatePrice({
 *   PlanId: "1f6a43a6-d2db-4343-9e2c-bceb9a5e1279",
 *   PlanItemId: "94cf0ef7-a5ee-4116-8fdd-b9e06ffeb503"
 * });
 */
export function usePlanUpdatePrice(request: GetPlanUpdatePriceRequest | null) {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['plan-update-price', request?.PlanId, request?.PlanItemId],
        queryFn: () => {
            if (!request) {
                throw new Error('Request is required');
            }
            return getPlanUpdatePrice(request, configurationSettings);
        },
        enabled: !!configurationSettings && !!request?.PlanId && !!request?.PlanItemId,
        staleTime: 1 * 60 * 1000, // 1 minute
        retry: 1,
    });
}